import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { formatPositionAge } from "../../utils/scoutFormat";
import { clubRadarService } from "../../services/clubRadarService";
import { ListRow, InlineNewList } from "../club-radar/ClubRadarListPickRows";

// Close-request dialog (design 6c — binding over the simpler 1c, DECISIONS D-004).
// Asks what happens to radar players: keep them (default, nothing lost by accident)
// vs remove with the request (a soft-remove server-side; the design's "deleted" is
// state='removed'). Attribution + notes survive either way.
//
// DECISION-002 — the keep branch used to promise "the radar's long-term list". Radar
// V2 has no such surface: rule R1 abolished it ("pure watchlist, lists only"), so the
// dialog was selling a place that had been deliberately removed. It now lets the club
// CHOOSE where the players stay — an existing radar list or one created right here —
// with the default My Radar preselected. No new tab and no new surface: the rows and
// the inline "+ New list" are the club radar's OWN picker components
// (club-radar/ClubRadarListPickRows, shared with the Add-to-Radar popover), and a new
// list is created through the same clubRadarService.createList every other surface
// uses. Nothing is chosen for a user who cannot see lists (no access_radar → the
// lists call 403s) — the server then falls back to their default My Radar.
const RadioRow = ({ selected, onSelect, title, body, children }) => (
  <div
    className={`w-full rounded-xl border transition ${
      selected ? "border-[#2283FF] bg-[#E8F1FF]/40" : "border-[#E5E7EB]"
    }`}
  >
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 ${selected ? "" : "hover:bg-gray-50 rounded-xl"}`}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
            selected ? "border-[#2283FF]" : "border-[#D1D5DB]"
          }`}
        >
          {selected && <span className="w-2 h-2 rounded-full bg-[#2283FF]" />}
        </span>
        <div>
          <p className="text-[13.5px] font-semibold text-[#111827]">{title}</p>
          <p className="text-[12px] text-[#6B7280] mt-0.5 leading-snug">{body}</p>
        </div>
      </div>
    </button>
    {children}
  </div>
);

const CloseScoutRequestDialog = ({ request, onCancel, onConfirm, busy }) => {
  const [keep, setKeep] = useState(true); // keep = default (D-004)
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState(null);
  const [creating, setCreating] = useState(false);
  const radarCount = request?.counts?.on_radar ?? 0;
  const wantsLists = !!request && radarCount > 0;

  // Load the club's radar lists once the dialog is up and there is anything to
  // place. The default My Radar comes preselected, so "no choice" and "My Radar"
  // are the same thing — exactly what the server does without a target_list_id.
  useEffect(() => {
    if (!wantsLists) return undefined;
    const controller = new AbortController();
    (async () => {
      const res = await clubRadarService.getLists(controller.signal);
      if (!res.success) return; // no access_radar → no picker, server picks the default
      const all = (res.data?.my_lists || []).concat(res.data?.shared_with_club || []);
      setLists(all);
      const preferred = all.find((l) => l.is_default) || all[0];
      if (preferred) setListId(preferred.id);
    })().catch(() => {});
    return () => controller.abort();
  }, [wantsLists, request?.id]);

  if (!request) return null;

  const title = formatPositionAge(request);
  const playerWord = radarCount === 1 ? "player" : "players";

  const createList = async (name) => {
    setCreating(true);
    const res = await clubRadarService.createList({ name });
    setCreating(false);
    if (!res.success || !res.data?.id) {
      toast.error(res.error || "Couldn't create the list.");
      return false;
    }
    setLists((cur) => cur.concat(res.data));
    setListId(res.data.id); // a list made here is the one they meant
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 flex flex-col gap-4">
        <div>
          <h3 className="text-[16px] font-bold text-[#111827]">Close scout request?</h3>
          <p className="text-[13px] text-[#6B7280] mt-1.5 leading-relaxed">
            Agents can no longer see or answer &ldquo;{title}&rdquo;. You have{" "}
            <strong className="text-[#111827]">
              {radarCount} {playerWord} on the radar
            </strong>{" "}
            from this request.
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          <RadioRow
            selected={keep}
            onSelect={() => setKeep(true)}
            title="Keep them on your radar"
            body="Choose the list they stay in. Notes and recommenders stay attached."
          >
            {keep && lists.length > 0 && (
              <div className="px-3 pb-3">
                <p className="text-[10.5px] font-bold tracking-[.07em] text-[#9CA3AF] uppercase px-1 pb-0.5">
                  List
                </p>
                <div
                  data-testid="close-dialog-list-scroll"
                  className="bg-white rounded-xl border border-[#E5E7EB] px-1 py-1"
                  style={{ maxHeight: 168, overflowY: "auto", overscrollBehavior: "contain" }}
                >
                  {lists.map((l) => (
                    <ListRow
                      key={l.id}
                      list={l}
                      checked={listId === l.id}
                      onToggle={setListId}
                      role="radio"
                    />
                  ))}
                </div>
                <InlineNewList creating={creating} onCreateList={createList} />
              </div>
            )}
          </RadioRow>
          <RadioRow
            selected={!keep}
            onSelect={() => setKeep(false)}
            title="Remove them with the request"
            body="The radar entries are deleted. Recommenders aren't notified."
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-[#E5E7EB] text-[13px] font-semibold text-[#111827] hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(keep, keep ? listId : null)}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-[#DC2626] text-white text-[13px] font-semibold hover:bg-[#B91C1C] disabled:opacity-50"
          >
            {busy ? "Closing…" : "Close request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloseScoutRequestDialog;
